import { FOLLOW_UP_QUESTION_IDENTIFIER } from '@/constants';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { logger } from '@/utils/usefulFuncs';

const blankArray = [];
const PLUGINS = {
  codeSyntax: 'rehypeHighlight',
  gfm: 'remarkGfm',
  html: 'rehypeRaw',
  math: 'remarkMath',
  katex: 'rehypeKatex',
};
const pluginLookup = {
  [PLUGINS.codeSyntax]: rehypeHighlight,
  [PLUGINS.gfm]: remarkGfm,
  [PLUGINS.html]: rehypeRaw,
  [PLUGINS.math]: remarkMath,
  [PLUGINS.katex]: rehypeKatex,
};

export class AnswerFormatter {
  constructor() {
    this._citationMockURL = 'https://example.com/citation';
  }

  detectPlugins = ({ answer, plugins, ignoreLatex }) => {
    if (!answer) return [false, blankArray];

    const formattedResult = this.formatResult(answer, ignoreLatex);
    const detectedPlugins = this._detectPluginsFromResult(formattedResult);
    const displayPlugins = this._convertPluginNamesToFunc(detectedPlugins);
    if (!this._samePlugins(displayPlugins, plugins)) {
      return [true, displayPlugins];
    }
    return [false, plugins];
  };

  getPluginsForResult = (result, ignoreLatex = false) => {
    if (!result) return blankArray;
    const detectedPlugins = this._detectPluginsFromResult(result);
    return this._convertPluginNamesToFunc(detectedPlugins);
  };

  formatResult = (result, ignoreLatex = false) => {
    if (!result) return result;

    const v = this.removeFollowUpQuestions(result);
    let withFixedLatex = v;
    if (!ignoreLatex) {
      withFixedLatex = this._fixLatexDelimiters(v);
    }

    // Check if there's LaTeX math in the content
    const hasLatexMath = withFixedLatex.indexOf('$$') > -1 || withFixedLatex.indexOf('\\begin{') > -1;

    // If there's LaTeX math, we need to escape dollar signs OUTSIDE math blocks
    if (hasLatexMath && !ignoreLatex) {
      // Only handle $$ blocks and fix LaTeX issues within them
      const result = withFixedLatex.replace(
        /(\$\$[\s\S]*?\$\$)|(?<!\\)(\$\d+(?:,\d{3})*(?:\.\d+)?)/g,
        (match, mathBlock, currency) => {
          if (mathBlock) {
            // Extract content between $$, fix issues, then reassemble
            const content = mathBlock.slice(2, -2);
            const fixed = this._fixLatexIssues(content);
            return '$$' + fixed + '$$';
          } else if (currency) {
            return '\\' + currency;
          }
          return match;
        }
      );

      return result;
    }

    return this._escapeDollarSigns(withFixedLatex);
  };

  parseFollowUpQuestions = (result) => {
    if (!result) return blankArray;
    const followUpSection = result.split(FOLLOW_UP_QUESTION_IDENTIFIER)[1];
    if (followUpSection) {
      return followUpSection.split('\n').filter((line) => line.trim() !== '');
    }
    return blankArray;
  };

  removeFollowUpQuestions = (result) => {
    if (!result) return result;

    try {
      return result.split(FOLLOW_UP_QUESTION_IDENTIFIER)[0];
    } catch (e) {
      logger('could not remove follow-up questions', e, result);
      return result;
    }
  };

  isCitationLink = (href) => {
    if (!href) return false;
    const link = href.slice(0, 28);
    return link === this._citationMockURL;
  };

  _detectPluginsFromResult = (result) => {
    if (!result) return blankArray;

    const plugins = [];

    const hasTable = result.indexOf('|') > -1;
    const hasStrike = result.indexOf('~~') > -1;
    if (hasTable || hasStrike) {
      plugins.push(PLUGINS.gfm);
    }

    const hasHTMLMarkdown = result.indexOf('<b') > -1;
    if (hasHTMLMarkdown) {
      plugins.push(PLUGINS.html);
    }
    const hasCodeSnip = result.indexOf('<code') > -1;
    if (hasCodeSnip) {
      plugins.push(PLUGINS.codeSyntax);
    }

    const hasKatexSnip = result.indexOf('$$') > -1 || result.indexOf('\\frac') > -1 || result.indexOf('\\[') > -1;
    if (hasKatexSnip) {
      plugins.push(PLUGINS.math);
      plugins.push(PLUGINS.katex);
    }
    return plugins;
  };

  _convertPluginNamesToFunc = (p) => {
    return p.map((n) => pluginLookup[n]);
  };

  // Function to fix LaTeX delimiter issues - convert bracket notation to $$
  _fixLatexDelimiters = (markdownText) => {
    if (!markdownText) return markdownText;

    let fixed = markdownText;

    // Fix [ \begin ... ] to $$\begin ... $$
    fixed = fixed.replace(/\[\s*(\\begin\{[^}]+\}[\s\S]*?\\end\{[^}]+\})\s*\]/g, (match, content) => {
      const escaped = content.replace(/\$/g, '\\$');
      return `$$\n${escaped}\n$$`;
    });

    // Handle single brackets around align environments
    fixed = fixed.replace(/\[\s*(\\align[\s\S]*?)\s*\]/g, (match, content) => {
      const escaped = content.replace(/\$/g, '\\$');
      return `$$\n${escaped}\n$$`;
    });

    // Fix simple bracket expressions like [ \text{...} ... ] to $$...$$
    fixed = fixed.replace(/\[\s*([^\[\]]*\\[^\[\]]*)\s*\]/g, (match, content) => {
      // Only replace if it looks like LaTeX (has backslash commands)
      if (content.includes('\\')) {
        const escaped = content.replace(/\$/g, '\\$');
        return `$$${escaped}$$`;
      }
      return match;
    });

    return fixed;
  };

  // Function to escape dollar signs in currency amounts, but leave LaTeX math intact
  _escapeDollarSigns = (markdownText) => {
    if (!markdownText) return markdownText;
    // Use negative lookbehind to avoid double-escaping already escaped dollars
    return markdownText.replace(/(?<!\\)(\$\d+(?:,\d{3})*(?:\.\d+)?)/g, '\\$&');
  };

  // Function to fix LaTeX-specific issues within math blocks
  _fixLatexIssues = (mathContent) => {
    if (!mathContent) return mathContent;
    let fixed = mathContent;

    // Fix single backslashes that should be double backslashes for line breaks in align environments
    // This regex looks for patterns like "\ &" or "\ \end" which indicate line breaks
    fixed = fixed.replace(/([^\\])\\ (&|\\end)/g, '$1\\\\ $2');

    // Fix standalone single backslash at end before closing tags
    fixed = fixed.replace(/([^\\])\\ (\\end\{align)/g, '$1\\\\ $2');

    // Fix malformed dollar signs in LaTeX (like \\$100 should be \$100 or just 100)
    // Remove extra backslashes before dollar signs in math mode
    fixed = fixed.replace(/\\\\(\$\d)/g, '\\$1');

    return fixed;
  };

  _samePlugins = (p1, p2) => {
    if (!p1 && !p2) return true;
    if (!p1 || !p2) return false;
    if (p1.length !== p2.length) return false;
    for (let i = 0; i < p1.length; i++) {
      if (p1[i] !== p2[i]) return false;
    }
    return true;
  };

  removeNumericOrderFromString = (str) => {
    let i = 0;
    while (i < str.length) {
      if (str[i] === '.' && i !== 0) {
        i++;
        while (i < str.length && str[i] === ' ') {
          i++;
        }
        return str.substring(i);
      } else if (!this.isDigit(str[i])) {
        return str;
      }
      i++;
    }

    return str;
  };

  removeBoldMarkdown = (str) => {
    return str.split('*').join('');
  };

  isDigit = (char) => {
    return char >= '0' && char <= '9';
  };
}

export const answerFormatter = new AnswerFormatter();
